<script lang="ts">
  import PickupOrDeliveryToggle from "@retailer/svelte/Cart/Page/PickupOrDelivery/PickupOrDeliveryToggle.svelte";
  import PickupOrDeliveryInfo from "@retailer/svelte/Cart/Page/PickupOrDelivery/PickupOrDeliveryInfo.svelte";
  import PickupLocationModal from "@retailer/svelte/Cart/Page/PickupOrDelivery/PickupLocationModal.svelte";
  import FulfillmentMethodOptions from "@retailer/svelte/Shared/FulfillmentMethodOptions.svelte";
  import type { CartProduct } from "@shared/types/ProductLine";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import * as PickupWarehousesService from "@retailer/services/pickup_warehouses_service";
  import { pickupWarehousesStore } from "@retailer/svelte/stores/PickupWarehousesStore";
  import { PickupOrDeliveryEntity } from "@shared/services/event_tracking/entities";
  import CurrentRetailerStore from "@retailer/svelte/stores/CurrentRetailerStore";
  import { InventoryItemStore } from "@retailer/svelte/CartWidget/v2/Stores/InventoryItemStore";
  import type { Distributor } from "@shared/types/Distributor";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import type { Warehouse } from "@shared/types/Warehouse";
  import { CraftAlertsStore } from "@shared/svelte/Components/Craft/CraftAlertsWrapper.svelte";
  import { isAccountNumberRequired } from "@retailer/services/product_card_service";

  const { trackMrcEvents, trackClick } = EventTracking;

  export let cartProduct: CartProduct;
  export let Cart: any;
  export let distributor: Partial<Distributor>;
  export let justify: string = "end";
  export let callback: () => void = () => {};

  let showPickupSelectionModal = false;
  let trackingEntity: PickupOrDeliveryEntity = PickupOrDeliveryEntity.fromCartProduct(cartProduct);
  let inFulfillmentEnhancementsRollout = stagedRolloutCheck(
    "marketplace__cart_page_fulfillment_enhancements",
  );
  let loaded: boolean = false;
  let warehouses: Warehouse[] = [];
  let defaultWarehouse: Warehouse = null;
  let taskPanelOpen: boolean = false;
  let skus: string[] = [];

  const wineCellarStagedRolloutEnabled = stagedRolloutCheck(
    "marketplace__wc_for_geolocation_and_account_based_distributors",
  );

  $: currentPickupLocation = { id: cartProduct.warehouse_id, name: cartProduct.warehouse_name };

  const generateTrackingEntity = (pickupLocationId: number | null) => {
    return PickupOrDeliveryEntity.fromCartProduct({
      ...cartProduct,
      warehouse_id: pickupLocationId,
    });
  };

  const getWarehousesAndShowModal = async () => {
    await PickupWarehousesService.get(cartProduct.product.id, cartProduct.distributor_id);
    showPickupSelectionModal = true;
    currentPickupLocation = { id: cartProduct.warehouse_id, name: cartProduct.warehouse_name };
  };

  const onDeliveryClick = () => {
    updateDeliveryOrPickupSelection(null);
    trackClick("pickup_or_delivery_select_delivery", generateTrackingEntity(null));
  };

  const onPickupClick = async () => {
    await getWarehousesAndShowModal();
    trackClick(
      "pickup_or_delivery_select_pickup",
      generateTrackingEntity(currentPickupLocation.id),
    );
  };

  const onChangePickupLocation = async () => {
    await getWarehousesAndShowModal();
    trackClick(
      "pickup_or_delivery_edit_pickup_selection",
      generateTrackingEntity(currentPickupLocation.id),
    );
  };

  const onPickupSelection = selectedPickupLocation => {
    updateDeliveryOrPickupSelection(selectedPickupLocation.id);
    trackClick("pickup_or_delivery_modal_save", generateTrackingEntity(selectedPickupLocation.id));
  };

  const onCancel = () => {
    closeModal();
    trackClick("pickup_or_delivery_modal_cancel", generateTrackingEntity(currentPickupLocation.id));
  };

  const onUpdate = () => {
    callback();
    closeModal();
    InventoryItemStore.fetchInventoryItem(cartProduct.inventory_id);
  };

  const updateDeliveryOrPickupSelection = (selectedPickupLocationId: number) => {
    Cart.updateDeliveryOrPickupLocation(
      {
        ...cartProduct,
        warehouse_id: selectedPickupLocationId,
      },
      onUpdate,
    );
  };

  const closeModal = () => {
    showPickupSelectionModal = false;
  };

  const triggerTaskPanel = () => {
    taskPanelOpen = !taskPanelOpen;
  };

  const triggerAlert = (event: CustomEvent) => {
    const warehouse = event.detail;

    CraftAlertsStore.add({
      message:
        warehouse.location_code === ""
          ? `You are now ordering for delivery for ${$CurrentRetailerStore.name}`
          : `You are now ordering for pickup at ${warehouse.name} for ${$CurrentRetailerStore.name}`,
      variant: "success",
    });
  };

  const saveTaskPanelSelection = async (event: CustomEvent) => {
    const warehouse = event.detail;
    await Cart.updateDeliveryOrPickupLocation({
      ...cartProduct,
      warehouse_location_code: warehouse.location_code || "",
    });
    triggerAlert(event);
  };

  const needsAccountNumber = async () => {
    if (!wineCellarStagedRolloutEnabled)
      return (
        distributor?.requires_account_number &&
        !distributor?.is_rolling_out_integrated_orders &&
        !distributor?.account_number
      );

    const sku = cartProduct.inventory?.sku;
    const response = await isAccountNumberRequired(
      distributor?.id,
      sku,
      cartProduct.warehouse_location_code,
      distributor?.account_number,
    );
    return response;
  };

  $: skus = cartProduct.inventory?.sku ? [cartProduct.inventory?.sku] : [];
</script>

{#if distributor?.uses_warehouse_pickup}
  <div use:trackMrcEvents={[[trackingEntity]]} class="craft">
    {#if inFulfillmentEnhancementsRollout && distributor?.uses_wine_cellar}
      {#await needsAccountNumber() then needsAccountNumber}
        {#if !needsAccountNumber}
          <PickupOrDeliveryInfo bind:currentPickupLocation on:openDrawer={triggerTaskPanel} />
          <FulfillmentMethodOptions
            bind:open={taskPanelOpen}
            {distributor}
            {skus}
            selectedStockSiteId={cartProduct.warehouse_location_code}
            useCustomSave={true}
            forProductLine={false}
            on:saveSelection={saveTaskPanelSelection}
          />
        {/if}
      {/await}
    {:else}
      <PickupOrDeliveryToggle
        productIdToEnsureUniqueSelectors={cartProduct.id}
        bind:currentPickupLocation
        {onDeliveryClick}
        {onPickupClick}
        {onChangePickupLocation}
        {justify}
      />
    {/if}
  </div>

  {#if showPickupSelectionModal && $pickupWarehousesStore.loaded}
    <PickupLocationModal
      selectedPickupLocationId={currentPickupLocation.id}
      pickupOptions={$pickupWarehousesStore.pickupWarehouses}
      {distributor}
      {onCancel}
      {onPickupSelection}
      shouldShowLowStock={true}
    />
  {/if}
{/if}
